import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import themes from '../../themes';
import { BidStatus, BidStatusName } from '../../types/auction';
import { VdpId } from '../../constants/vdp';

const DEFAULT_WIDTH = 700;
const DEFAULT_TIMEOUT_MS = 250;
const DEFAULT_PADDING = '24px';
const DEFAULT_BORDER_RADIUS = '3px';

const propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  width: PropTypes.number,
  padding: PropTypes.string,
  borderRadius: PropTypes.string,
  isClickOverlayClose: PropTypes.bool,
  isClickEscClose: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  bodyOpenClassName: PropTypes.string,
  variant: PropTypes.oneOf([
    BidStatusName[BidStatus.topBidder],
    BidStatusName[BidStatus.outbid],
    BidStatusName[BidStatus.none],
    'ompSearchOverlay',
    'trFullScreenSearchBar',
    'retailToolsEditModal'
  ]),
  onRequestClose: PropTypes.func,
  backgroundColor: PropTypes.string,
  overflow: PropTypes.string,
  testId: PropTypes.string,
  hasParentSelector: PropTypes.bool
};

const defaultProps = {
  width: DEFAULT_WIDTH,
  padding: DEFAULT_PADDING,
  borderRadius: DEFAULT_BORDER_RADIUS,
  isClickOverlayClose: false,
  isClickEscClose: false,
  isFullScreen: false,
  bodyOpenClassName: undefined,
  variant: undefined,
  onRequestClose: undefined,
  backgroundColor: themes.COLORS.BASE,
  overflow: 'auto',
  testId: undefined,
  hasParentSelector: false
};

/**
 * Variant Definitions
 */
const StyledVariants = {
  [BidStatusName[BidStatus.topBidder]]: {
    border: `2px solid ${themes.COLORS.LA_PALMA}`
  },
  [BidStatusName[BidStatus.outbid]]: {
    border: `2px solid ${themes.COLORS.MONZA}`
  },
  ompSearchOverlay: {
    width: '972px',
    minWidth: '900px',
    height: '91%',
    overflowX: 'hidden',
    overflowY: 'scroll',
    backgroundColor: themes.COLORS.NEUTRAL
  },
  trFullScreenSearchBar: {
    display: 'flex'
  },
  retailToolsEditModal: {
    width: '500px',
    overlay: {
      backgroundColor: themes.COLORS.MODAL_OVERLAY
    }
  }
};

const defaultStyles = {
  overlay: {
    display: 'flex',
    position: 'fixed',
    zIndex: 1000,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)'
  },
  content: {
    position: 'relative',
    zIndex: 10000,
    top: 'auto',
    bottom: 'auto',
    left: 'auto',
    right: 'auto',
    height: 'auto',
    width: '700px',
    backgroundColor: '#fff',
    boxShadow: '0px 9px 24px rgba(0, 0, 0, 0.2)',
    borderRadius: '3px',
    overflow: 'auto'
  }
};

const BaseModal = ({
  isOpen,
  isClickOverlayClose,
  isClickEscClose,
  isFullScreen,
  width,
  bodyOpenClassName,
  padding,
  borderRadius,
  variant,
  children,
  onAfterOpen,
  onAfterClose,
  onRequestClose,
  setRef,
  backgroundColor,
  overflow,
  testId,
  hasParentSelector
}) => {
  // Style Overrides
  const styles = { ...defaultStyles };

  if (isOpen) {
    styles.content.width = `${width}px`;
    styles.content.padding = padding;
    styles.content.borderRadius = borderRadius;
    styles.content.backgroundColor = backgroundColor;
    styles.content.overflow = overflow;

    if (variant) {
      if (variant === 'retailToolsEditModal') {
        styles.overlay.backgroundColor =
          StyledVariants.retailToolsEditModal.overlay.backgroundColor;
      }
      styles.content = {
        ...styles.content,
        ...StyledVariants[variant]
      };
    }

    if (isFullScreen) {
      styles.content = {
        ...styles.content,
        width: '100%',
        height: '100%',
        border: 'none'
      };
    }
    if (hasParentSelector) {
      styles.overlay.position = 'absolute';
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnOverlayClick={isClickOverlayClose}
      shouldCloseOnEsc={isClickEscClose}
      closeTimeoutMS={DEFAULT_TIMEOUT_MS}
      bodyOpenClassName={bodyOpenClassName}
      onAfterOpen={onAfterOpen}
      onAfterClose={onAfterClose}
      style={styles}
      appElement={document.getElementById('root')}
      onRequestClose={onRequestClose}
      contentRef={setRef}
      ariaHideApp={false}
      testId={testId}
      parentSelector={() =>
        hasParentSelector && document.querySelector(`#${VdpId}`)
          ? document.querySelector(`#${VdpId}`)
          : document.querySelector(`#root`)
      }
    >
      {children}
    </Modal>
  );
};

BaseModal.propTypes = propTypes;
BaseModal.defaultProps = defaultProps;

export default BaseModal;
